/**
 * Pure helpers for gTwin Structure Studio — slot tree, formatters, patch builders.
 * ADR-120 / WP-B.
 */

import type { GTwinRecord, ProjectAnchor, SceneSlot } from '@/sdk-stub/gtwin-sdk/types';
import type { SceneItemPatch } from '@/sdk-stub/gtwin-sdk/scene-items';

export const STRUCTURE_SLOTS: readonly SceneSlot[] = [
  'basemaps',
  'terrain',
  'realityData',
  'engineeringModels',
  'featureLayers',
  'overlays',
] as const;

export interface StudioSceneItemEntry {
  slot: SceneSlot;
  item: Record<string, unknown>;
}

export interface EnvelopeSummary {
  id: string;
  label: string;
  version: number;
  domain: string;
  anchorKind: string;
  horizontalCrs: string;
  displayCrs: string;
}

export interface ItemInspectorDraft {
  visible: boolean;
  title: string;
  opacity: number;
}

export interface AnchorInspectorDraft {
  kind: 'unknown' | 'projected' | 'linear';
  horizontalCrs: string;
  displayCrs: string;
}

export function extractStructureItems(record: GTwinRecord): StudioSceneItemEntry[] {
  const scene = (record.payload?.geospatial?.scene ?? {}) as unknown as Record<string, unknown>;
  const out: StudioSceneItemEntry[] = [];

  for (const slot of STRUCTURE_SLOTS) {
    if (slot === 'terrain') {
      const terrain = scene.terrain;
      if (terrain && typeof terrain === 'object' && 'id' in terrain) {
        out.push({ slot, item: terrain as Record<string, unknown> });
      }
      continue;
    }
    const items = scene[slot];
    if (!Array.isArray(items)) continue;
    for (const raw of items) {
      if (raw && typeof raw === 'object' && 'id' in raw) {
        out.push({ slot, item: raw as Record<string, unknown> });
      }
    }
  }
  return out;
}

export function extractLayerOrder(record: GTwinRecord): string[] {
  const geo = record.payload?.geospatial as unknown as Record<string, unknown> | undefined;
  const fromPayload = geo?.layerOrder;
  if (Array.isArray(fromPayload) && fromPayload.every((x) => typeof x === 'string')) {
    return [...fromPayload];
  }
  return extractStructureItems(record).map((e) => String(e.item.id));
}

export function formatEnvelopeSummary(record: GTwinRecord): EnvelopeSummary {
  const anchor = record.envelope?.projectAnchor;
  const kind = anchor?.kind ?? 'unknown';
  let horizontalCrs = '';
  let displayCrs = '';
  if (anchor && anchor.kind === 'projected') {
    horizontalCrs = anchor.horizontalCrs ?? '';
    displayCrs = anchor.displayCrs ?? anchor.horizontalCrs ?? '';
  }
  return {
    id: record.id,
    label: record.label ?? record.id,
    version: record.version,
    domain: record.domain ?? '',
    anchorKind: kind,
    horizontalCrs,
    displayCrs,
  };
}

export function itemDraftFromRecord(item: Record<string, unknown>): ItemInspectorDraft {
  const opacityRaw = item.opacity;
  const opacity =
    typeof opacityRaw === 'number' && Number.isFinite(opacityRaw) ? opacityRaw : 1;
  return {
    visible: item.visible !== false,
    title: typeof item.title === 'string' ? item.title : String(item.id ?? ''),
    opacity,
  };
}

export function anchorDraftFromRecord(record: GTwinRecord): AnchorInspectorDraft {
  const anchor = record.envelope?.projectAnchor;
  if (anchor?.kind === 'projected') {
    return {
      kind: 'projected',
      horizontalCrs: anchor.horizontalCrs ?? '',
      displayCrs: anchor.displayCrs ?? anchor.horizontalCrs ?? '',
    };
  }
  if (anchor?.kind === 'linear') {
    return { kind: 'linear', horizontalCrs: '', displayCrs: '' };
  }
  return { kind: 'unknown', horizontalCrs: '', displayCrs: '' };
}

export function buildItemPatch(
  itemId: string,
  draft: ItemInspectorDraft,
  baseline: ItemInspectorDraft,
): SceneItemPatch | null {
  const patch: SceneItemPatch = { id: itemId };
  let changed = false;
  if (draft.visible !== baseline.visible) {
    patch.visible = draft.visible;
    changed = true;
  }
  if (draft.title !== baseline.title) {
    patch.title = draft.title;
    changed = true;
  }
  if (draft.opacity !== baseline.opacity) {
    patch.opacity = draft.opacity;
    changed = true;
  }
  return changed ? patch : null;
}

export function buildProjectAnchorFromDraft(
  current: ProjectAnchor | undefined,
  draft: AnchorInspectorDraft,
): ProjectAnchor | null {
  if (draft.kind !== 'projected') return null;
  const prev =
    current?.kind === 'projected'
      ? current
      : ({ kind: 'projected' as const, horizontalCrs: draft.horizontalCrs });
  const next: ProjectAnchor = {
    kind: 'projected',
    horizontalCrs: draft.horizontalCrs.trim() || prev.horizontalCrs,
    displayCrs: draft.displayCrs.trim() || draft.horizontalCrs.trim() || prev.displayCrs,
    displayCrsProj4:
      current?.kind === 'projected' ? current.displayCrsProj4 : undefined,
    verticalDatum: current?.kind === 'projected' ? current.verticalDatum : undefined,
    units: current?.kind === 'projected' ? current.units : undefined,
  };
  if (
    current?.kind === 'projected' &&
    current.horizontalCrs === next.horizontalCrs &&
    (current.displayCrs ?? current.horizontalCrs) === (next.displayCrs ?? next.horizontalCrs)
  ) {
    return null;
  }
  return next;
}

export function moveLayerInOrder(
  order: readonly string[],
  itemId: string,
  direction: 'up' | 'down',
): string[] | null {
  const idx = order.indexOf(itemId);
  if (idx < 0) return null;
  const swapWith = direction === 'up' ? idx - 1 : idx + 1;
  if (swapWith < 0 || swapWith >= order.length) return null;
  const next = [...order];
  [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
  return next;
}

export function formatEndpointList(endpoints: unknown): string {
  if (!endpoints || typeof endpoints !== 'object') return '(none)';
  return Object.entries(endpoints as Record<string, unknown>)
    .map(([key, val]) => {
      if (typeof val === 'string') return `${key}: ${val}`;
      if (val && typeof val === 'object' && 'href' in val) {
        return `${key}: ${String((val as { href?: unknown }).href ?? '')}`;
      }
      return `${key}: (ref)`;
    })
    .join('\n');
}
